import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage, ProtectedRoute, useAuthStore } from './core/auth';
import { ProjectDashboard } from './core/dashboard/ProjectDashboard';
import { SuperAdminDashboard } from './core/dashboard/SuperAdminDashboard';
import { SuperAdminLayout } from './core/layouts';
import { ProjectForm, ProjectProvider, NoProjectsAvailable } from './core/projects';
import { ProjectEntryPage } from './core/projects/ProjectEntryPage';
import { ParticipationPage } from './core/participation/ParticipationPage';
import { ResultsPage } from './core/participation/ResultsPage';
import { ValorSanJuanPublicPage } from './core/realestate/ValorSanJuanPublicPage';
import { VsjExperienceShell } from './core/realestate/experience/VsjExperienceShell';
import { VsjReportPage } from './core/realestate/experience/VsjReportPage';
import { ContributionsAdminPage } from './core/participation/admin/ContributionsAdminPage';
import { AdminEntryPage } from './core/public/AdminEntryPage';
import { WaylineEntryPage } from './core/public/WaylineEntryPage';
import { SuperadminOverview } from './core/superadmin/SuperadminOverview';
import { ClientForm } from './core/tenants/ClientForm';
import { ToastProvider } from './core/toast';
import { UIScaleProvider } from './core/ui-scale';
import { UsersList, UserForm } from './core/users';
import {
  AnalysisWorkspace,
  CollaborationWorkspace,
  InventoryWorkspace,
  OperationsWorkspace,
  ReportsWorkspace,
  ScenariosWorkspace,
  SurveyWorkspace,
  TerritoryWorkspace,
} from './core/workspaces';
import { LoaderPreview } from './dev/LoaderPreview';

// ─── Subdomain detection ──────────────────────────────────────────────────────

type SubdomainMode =
  | { mode: 'admin' }
  | { mode: 'project'; subdomain: string }
  | { mode: 'vsj-public' }
  | { mode: 'normal' };

/**
 * Detecta el modo de acceso según el hostname:
 * - admin.wayline.city           → modo superadmin
 * - {project}.wayline.city       → modo proyecto (subdomain = slug del proyecto)
 * - localhost / IP / raíz        → modo normal (rutas internas)
 */
function detectSubdomainMode(): SubdomainMode {
  const hostname = window.location.hostname;

  // Dev environments — rutas internas normales
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    /^192\.168\./.test(hostname) ||
    /^10\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(hostname)
  ) {
    return { mode: 'normal' };
  }

  // admin.wayline.city
  if (hostname.startsWith('admin.')) {
    return { mode: 'admin' };
  }

  // valorsanjuan.com.ar — portal público Valor San Juan (sin login ni suite interna)
  if (hostname === 'valorsanjuan.com.ar' || hostname.endsWith('.valorsanjuan.com.ar')) {
    return { mode: 'vsj-public' };
  }

  // {project}.wayline.city — cualquier subdominio distinto de www
  const match = /^([a-z0-9-]+)\.wayline\.city$/.exec(hostname);
  if (match && match[1] !== 'www') {
    return { mode: 'project', subdomain: match[1] };
  }

  return { mode: 'normal' };
}

// ─── Redirect por rol ─────────────────────────────────────────────────────────

/**
 * Redirect inicial sensible al rol.
 *
 * Espera a que se cargue el usuario para evitar flash.
 * - superadmin (role 'superadmin') → /admin/overview
 * - cualquier otro usuario autenticado      → /territorio
 * - no autenticado                          → /login
 */
function RoleAwareRedirect(): JSX.Element {
  const { isAuthenticated, isLoading, token, user } = useAuthStore();

  if (token && (isLoading || !user)) {
    return <></>;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const isSuperAdmin = user.role.name === 'superadmin';

  return <Navigate to={isSuperAdmin ? '/admin/overview' : '/territorio'} replace />;
}

// ─── Bloques de rutas reutilizables ──────────────────────────────────────────

/** Rutas de administración (superadmin + admin). */
function AdminRoutes(): JSX.Element {
  return (
    <>
      {/* Entrada principal del superadmin — Consola Territorial */}
      <Route
        path="/admin/overview"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <SuperAdminLayout>
              <SuperadminOverview />
            </SuperAdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <SuperAdminLayout>
              <SuperAdminDashboard />
            </SuperAdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/new"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <ProjectForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/:id/edit"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <ProjectForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/new"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <ClientForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:id"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <ClientForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <SuperAdminLayout>
              <UsersList />
            </SuperAdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/new"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <SuperAdminLayout>
              <UserForm />
            </SuperAdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id/edit"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <SuperAdminLayout>
              <UserForm />
            </SuperAdminLayout>
          </ProtectedRoute>
        }
      />
    </>
  );
}

/** Rutas de workspaces especializados. */
function WorkspaceRoutes(): JSX.Element {
  return (
    <>
      {/* /workspaces/territory legacy — redirige a /territorio/explorador */}
      <Route
        path="/workspaces/territory"
        element={<Navigate to="/territorio/explorador" replace />}
      />
      <Route
        path="/workspaces/survey"
        element={
          <ProtectedRoute>
            <SurveyWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspaces/inventory"
        element={
          <ProtectedRoute>
            <InventoryWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspaces/analysis"
        element={
          <ProtectedRoute>
            <AnalysisWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspaces/operations"
        element={
          <ProtectedRoute>
            <OperationsWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspaces/scenarios"
        element={
          <ProtectedRoute>
            <ScenariosWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspaces/reports"
        element={
          <ProtectedRoute>
            <ReportsWorkspace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspaces/collaboration"
        element={
          <ProtectedRoute>
            <CollaborationWorkspace />
          </ProtectedRoute>
        }
      />
    </>
  );
}

// ─── Routers por modo de subdominio ──────────────────────────────────────────

/**
 * admin.wayline.city — solo muestra rutas de administración.
 * El login se resuelve en AdminEntryPage → /login?next=/admin/overview.
 */
function AdminSubdomainRouter(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<AdminEntryPage />} />
      <Route path="/login" element={<LoginPage />} />
      {AdminRoutes()}
      <Route path="*" element={<AdminEntryPage />} />
    </Routes>
  );
}

/**
 * {project}.wayline.city — muestra el shell del proyecto resuelto por subdominio.
 * Usa `getBySubdomain` para resolver contra el campo `public_subdomain` del perfil
 * público, que puede diferir del slug interno del proyecto.
 * No hay selector de proyecto ni rutas globales expuestas.
 */
function ProjectSubdomainRouter({ subdomain }: { subdomain: string }): JSX.Element {
  return (
    <Routes>
      <Route
        path="*"
        element={<ProjectEntryPage initialSlug={subdomain} useSubdomainLookup />}
      />
    </Routes>
  );
}

/**
 * valorsanjuan.com.ar — renderiza directamente el portal público de Valor San Juan.
 * Sin login, sin suite interna, sin marca Wayline como principal.
 */
function VsjPublicRouter(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<VsjExperienceShell />} />
      <Route path="/asistente" element={<VsjExperienceShell />} />
      <Route path="/informe/:reportToken" element={<VsjReportPage />} />
      <Route path="/portal" element={<ValorSanJuanPublicPage />} />
      <Route path="/:section" element={<ValorSanJuanPublicPage />} />
    </Routes>
  );
}

/** Árbol de rutas estándar (localhost / wayline.city raíz). */
function NormalRouter(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<WaylineEntryPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin" element={<AdminEntryPage />} />
      {/* Valor San Juan — public real-estate portal, no auth required.
          Must be before /p/:projectSlug catch-all. */}
      {/* VSJ navigable experience prototype — must be before :section catch-all */}
      <Route path="/p/valor-san-juan/asistente" element={<VsjExperienceShell />} />
      <Route path="/p/valor-san-juan/informe/:reportToken" element={<VsjReportPage />} />
      <Route path="/p/valor-san-juan" element={<ValorSanJuanPublicPage />} />
      <Route path="/p/valor-san-juan/:section" element={<ValorSanJuanPublicPage />} />
      {/* Public participation — must be before /p/:projectSlug to avoid catch */}
      <Route path="/p/:projectSlug/participa" element={<ParticipationPage />} />
      {/* Technical results — institutional layout, self-protected (no Wayline login redirect) */}
      <Route path="/p/:projectSlug/resultados" element={<ResultsPage />} />
      {/* Internal contributions dashboard — superadmin only */}
      <Route
        path="/admin/projects/:projectId/contributions"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <ContributionsAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contributions/sclc"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <ContributionsAdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="/p/:projectSlug" element={<ProjectEntryPage />} />
      <Route path="/dev/loader" element={<LoaderPreview />} />

      {AdminRoutes()}
      {WorkspaceRoutes()}

      {/* Entrada territorial principal */}
      <Route
        path="/territorio"
        element={
          <ProtectedRoute>
            <ProjectDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/territorio/explorador"
        element={
          <ProtectedRoute>
            <TerritoryWorkspace />
          </ProtectedRoute>
        }
      />

      {/* Legacy redirects */}
      <Route path="/dashboard" element={<RoleAwareRedirect />} />
      <Route
        path="/no-projects"
        element={
          <ProtectedRoute>
            <NoProjectsAvailable />
          </ProtectedRoute>
        }
      />
      <Route path="/main" element={<Navigate to="/workspaces/inventory" replace />} />

      {/* Fallback */}
      <Route path="*" element={<RoleAwareRedirect />} />
    </Routes>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

/**
 * Root component de la aplicación Wayline.
 *
 * Detecta el subdominio al iniciar y enruta al shell correcto:
 * - admin.wayline.city       → AdminSubdomainRouter
 * - {project}.wayline.city   → ProjectSubdomainRouter
 * - localhost / raíz          → NormalRouter
 */
function App(): JSX.Element {
  const { loadUser, token } = useAuthStore();
  const subdomainMode = detectSubdomainMode();

  useEffect(() => {
    if (token) {
      void loadUser();
    }
  }, [token, loadUser]);

  return (
    <UIScaleProvider>
      <ToastProvider>
        <ProjectProvider>
          <BrowserRouter>
            {subdomainMode.mode === 'admin' && <AdminSubdomainRouter />}
            {subdomainMode.mode === 'project' && (
              <ProjectSubdomainRouter subdomain={subdomainMode.subdomain} />
            )}
            {subdomainMode.mode === 'vsj-public' && <VsjPublicRouter />}
            {subdomainMode.mode === 'normal' && <NormalRouter />}
          </BrowserRouter>
        </ProjectProvider>
      </ToastProvider>
    </UIScaleProvider>
  );
}

export default App;
